import React, { useMemo } from 'react'
import { Form, Formik } from 'formik'
import { Box, Typography } from '@mui/material'
import { toFormikValidationSchema } from 'zod-formik-adapter'
import { PatientHistoryType } from 'src/graphql.generated'
import useAddPatientHistoryEntry from 'src/participantHistory/useAddPatientHistoryEntry'
import { useToasts } from 'react-toast-notifications'
import _ from 'lodash'
import checkIfAutofillEnabled from 'src/participantHistory/checkIfAutofillEnabled'
import { Loader } from 'src/designSystem'
import { useLoadedNoteDraft } from 'src/noteDraft/useNoteDraft'
import { NoteDraftLoadError } from 'src/noteDraft/NoteDraftAlerts'
import {
  INTAKE_NOTE_COMPREHENSIVE_FORM_SCHEMA,
  IntakeNoteComprehensiveFormType,
} from './IntakeNoteComprehensiveFormSchema'
import ConfirmNoteDialog from '../ConfirmNoteDialog'
import { clearFormFromLocalStorage } from '../persistFormHelpers'
import FormPersistor from '../FormPersistor'
import { IntakeNoteComprehensiveFields } from './IntakeNoteComprehensiveFields'
import { getDefaultValues } from './initialValues/defaultValues'
import { mapPatientAnswersToForm } from './initialValues/patientAnswersTransform'
import { mapHealthQuestionnaireToForm } from './initialValues/healthQuestionnaireTransform'
import { mapOverallInformationToForm } from './initialValues/overallInformationTransform'
import { useGetIntakeNoteComprehensivePatientData } from './initialValues/useGetIntakeNoteComprehensivePatientData'
import AutofillIntakeNoteComprehensiveButton from './AutofillIntakeNoteComprehensiveButton'

const FORM_TYPE = PatientHistoryType.IntakeNoteComprehensive

const IntakeNoteComprehensiveForm = ({
  uid,
  afterSubmittedCallback,
}: {
  uid: string
  afterSubmittedCallback?: () => void
}) => {
  const addPatientHistoryEntry = useAddPatientHistoryEntry()

  const { addToast } = useToasts()

  const { data: intakeNoteComprehensivePatientData, refetch, loading } = useGetIntakeNoteComprehensivePatientData(uid)

  const loaded = useLoadedNoteDraft({ uid, type: FORM_TYPE })

  const initialFormValues = useMemo(() => {
    if (loaded.draft) {
      return loaded.draft
    }

    const defaultValues = getDefaultValues()
    const overallInformation = mapOverallInformationToForm(intakeNoteComprehensivePatientData?.overallInformation)
    const healthQuestionnaire = mapHealthQuestionnaireToForm(intakeNoteComprehensivePatientData?.healthQuestionnaire)
    const patientAnswersFormData = mapPatientAnswersToForm(intakeNoteComprehensivePatientData?.patientAnswers?.data)

    const mergedValues = _.merge(
      {},
      defaultValues,
      overallInformation,
      {
        REASON_FOR_SEEKING_TREATMENT: {
          HEALTH_QUESTIONNAIRE: healthQuestionnaire,
        },
      },
      patientAnswersFormData
    )
    return mergedValues
  }, [
    loaded.draft,
    intakeNoteComprehensivePatientData?.healthQuestionnaire,
    intakeNoteComprehensivePatientData?.overallInformation,
    intakeNoteComprehensivePatientData?.patientAnswers?.data,
  ])

  if (loaded.failed) {
    return <NoteDraftLoadError />
  }

  if (!loaded.ready) {
    return <Loader size="large" />
  }

  return (
    <Formik<IntakeNoteComprehensiveFormType>
      validationSchema={toFormikValidationSchema(INTAKE_NOTE_COMPREHENSIVE_FORM_SCHEMA)}
      initialValues={initialFormValues as IntakeNoteComprehensiveFormType}
      enableReinitialize
      onSubmit={async (values) => {
        try {
          await addPatientHistoryEntry({
            uid,
            date: values.date,
            type: PatientHistoryType.IntakeNoteComprehensive,
            value: values,
          })

          clearFormFromLocalStorage(uid, PatientHistoryType.IntakeNoteComprehensive)
          afterSubmittedCallback?.()
        } catch (e) {
          addToast('Failed to submit intake note and create a patient history entry', {
            appearance: 'error',
          })
        }
      }}
    >
      {({ isSubmitting, errors, submitForm }) => (
        <Form>
          {checkIfAutofillEnabled() && (
            // Loading data reinitializes the form, which would wipe an autofill made before it lands.
            <AutofillIntakeNoteComprehensiveButton uid={uid} disabled={loading} sx={{ mt: 2, mb: 2 }} />
          )}

          <FormPersistor uid={uid} formType={FORM_TYPE} />
          <IntakeNoteComprehensiveFields refetch={refetch} />

          <Box mt={5}>
            <ConfirmNoteDialog isSubmitting={isSubmitting} onSubmit={submitForm} />
          </Box>

          {Object.values(errors).filter((error) => error).length > 0 && (
            <Box mt={3}>
              <Typography variant="body1" color="error">
                Fill in required fields.
              </Typography>
            </Box>
          )}
        </Form>
      )}
    </Formik>
  )
}

export default IntakeNoteComprehensiveForm
